/**
 * What a translation session costs per hour, for either managed engine.
 *
 * ONE MODEL FOR BOTH ENGINES, PER TRANSLATED DIRECTION. Soniox and Qwen both
 * bill for the same three things: the audio a connection takes in for as long
 * as it is open, the text it writes while someone speaks, and the audio it
 * reads aloud. Each engine therefore reduces to three hourly rates
 * (`EngineRates`), and every setting is priced from them the same way.
 *
 * A SHARE OF THE HOUR BELONGS TO ONE PERSON. The calculator this replaced took
 * one "time someone is speaking" figure and split it among the translated
 * sides, treating a side left untranslated as silent. Turning a side on then
 * halved the other side's speech, and with it the voice that side was charged
 * for, so the price went DOWN (Qwen at 95%: $3.49 for "read to me" alone,
 * $2.59 with "my subtitles" added). Here each translated person speaks for a
 * share of the hour of their own, and turning a side on only ever adds that
 * side's cost.
 */
import {
  qwenProviderFor,
  sonioxProvider,
  type PricingResponse,
  type ProviderPricing,
  type QwenProviderPricing,
} from './pricing-client';

export type Engine = 'soniox' | 'qwen';

/** The engine switch's order. */
export const ENGINES: readonly Engine[] = ['soniox', 'qwen'];

/** Which of Alibaba's two sites serves managed Qwen: `sg` is Singapore, `cn` is Beijing. */
export type QwenRegion = 'sg' | 'cn';

/** The region switch's order. */
export const QWEN_REGIONS: readonly QwenRegion[] = ['sg', 'cn'];

/** Singapore: where managed Qwen ran alone until Beijing, so what an unmarked session means. */
export const DEFAULT_QWEN_REGION: QwenRegion = 'sg';

export type Side = 'them' | 'me';

export type Output = 'off' | 'text' | 'voice';

/** What each side's words become, and how much of the hour each person speaks, in whole percents. */
export interface CalcSettings {
  them: Output;
  me: Output;
  themShare: number;
  meShare: number;
  /** The engine sees one window, tab or screen (Qwen only). Unset is off; no preset sets it. */
  screenContext?: boolean;
}

/** USD per hour: per connected direction, per hour the translated person speaks, per hour read aloud. */
export interface EngineRates {
  connection: number;
  text: number;
  voice: number;
  /**
   * Per hour the translated person speaks, with screen context on. Only Qwen has it, and only
   * from a payload that publishes it: a Soniox rate set and a cached Qwen one leave it out.
   */
  screen?: number;
}

const PER_MILLION = 1_000_000;
const SECONDS_PER_HOUR = 3600;

const finiteOrNull = (rates: EngineRates): EngineRates | null =>
  Object.values(rates).every(Number.isFinite) ? rates : null;

export function sonioxRates(p: ProviderPricing): EngineRates {
  const r = p.tokenRatesUsdPerMillion;
  const c = p.tokenConversions;
  return {
    connection: (c.sttInputAudioPerConnectedHour * r.sttInputAudio) / PER_MILLION,
    text: (c.sttOutputTextPerSpeechHour * r.sttOutputText) / PER_MILLION,
    voice:
      (c.ttsInputTextPerGeneratedHour * r.ttsInputText +
        c.ttsOutputAudioPerGeneratedHour * r.ttsOutputAudio) /
      PER_MILLION,
  };
}

/**
 * Null when a conversion is missing: a browser can still hold a cached
 * `/api/pricing` body from before the text rate was published, and a price
 * built from `undefined` would render as NaN instead of as "unavailable".
 *
 * The screen-context add-on is read as published (`screenContextUsdPerHour`),
 * never recomputed from the image rate: that figure is the rounded add-on a
 * session's grant is sized from, so a page that multiplied the rate out itself
 * could disagree with it in the last decimals. The bill is not made from it:
 * a session is charged its `video_tokens` × the list image price × K. A body
 * without it prices everything else and leaves `screen` out, which is also
 * what hides the calculator's switch.
 */
export function qwenRates(p: QwenProviderPricing): EngineRates | null {
  const r = p.tokenRatesUsdPerMillion;
  const c = p.tokenConversions;
  const perHour = (tokensPerSecond: number, usdPerMillion: number) =>
    (tokensPerSecond * SECONDS_PER_HOUR * usdPerMillion) / PER_MILLION;
  const rates = finiteOrNull({
    connection: perHour(c.audioInTokensPerSecond, r.audioIn),
    text: perHour(c.textOutTokensPerSecondOfSpeech, r.textOut),
    voice: perHour(c.audioOutTokensPerSecondOfSpeech, r.audioOut),
  });
  return rates && Number.isFinite(p.screenContextUsdPerHour)
    ? { ...rates, screen: p.screenContextUsdPerHour }
    : rates;
}

/**
 * The selected engine's rates from the published payload, or null without them. Qwen is priced
 * from its region's own entry, so Beijing has none while the payload lists none; Soniox has no
 * region and ignores it.
 */
export function engineRates(
  pricing: PricingResponse | null,
  engine: Engine,
  region: QwenRegion,
): EngineRates | null {
  if (engine === 'qwen') {
    const qwen = qwenProviderFor(pricing, region);
    return qwen ? qwenRates(qwen) : null;
  }
  const soniox = sonioxProvider(pricing);
  return soniox ? finiteOrNull(sonioxRates(soniox)) : null;
}

/**
 * The region the payload can price: the given one when its entry is listed, Singapore when the
 * payload is loaded and lists none (Beijing's entry is absent while no USD/CNY rate is stored), and
 * the given one while the payload is still loading, so nothing flips to Singapore before the answer.
 * Nothing is rewritten: the visitor's pick and its storage keep Beijing for when the rate returns.
 */
export function effectiveQwenRegion(pricing: PricingResponse | null, region: QwenRegion): QwenRegion {
  if (!pricing) return region;
  return qwenProviderFor(pricing, region) ? region : DEFAULT_QWEN_REGION;
}

export interface CostParts {
  connections: number;
  connection: number;
  text: number;
  voice: number;
  /** The screen-context add-on: 0 unless the switch is on and the engine has a rate for it. */
  screen: number;
}

export function costParts(rates: EngineRates, s: CalcSettings, sharedStream: boolean): CostParts {
  const sides = [
    { output: s.them, share: s.themShare / 100 },
    { output: s.me, share: s.meShare / 100 },
  ].filter((side) => side.output !== 'off');
  // Face to face runs both directions over one shared stream: one connection.
  const connections = sharedStream && sides.length > 1 ? 1 : sides.length;
  const spoken = sides.reduce((sum, side) => sum + side.share, 0);
  const voiced = sides
    .filter((side) => side.output === 'voice')
    .reduce((sum, side) => sum + side.share, 0);
  return {
    connections,
    connection: rates.connection * connections,
    text: rates.text * spoken,
    voice: rates.voice * voiced,
    // Per direction that is on, for the hour its person speaks, like text.
    screen: s.screenContext && rates.screen !== undefined ? rates.screen * spoken : 0,
  };
}

export function partsTotal(p: CostParts): number {
  return p.connection + p.text + p.voice + p.screen;
}

/**
 * Round a USD figure to `decimals`, the ONE rounding rule the card displays
 * with. Separate from `toFixed`, which both rounds and formats: a value rounded
 * once for the headline and again for a row can land either side of a
 * half-cent for reasons no reader can see.
 */
export function roundUsd(usd: number, decimals: number): number {
  const scale = 10 ** decimals;
  return Math.round(usd * scale) / scale;
}

/**
 * The parts, rounded so they still add up to the rounded whole.
 *
 * The card prints the parts directly under the total, and parts rounded one by
 * one need not sum to it; a cent that visibly goes missing under a headline
 * reads as an error in the bill. LARGEST REMAINDER: floor every part, then hand
 * the leftover units one at a time to the parts with the largest discarded
 * fraction, so no part moves more than the rounding it was already subject to.
 *
 * Remainders are quantised before comparison and ties go to the largest part:
 * `0.144 * 100` is 14.399999999999999 while `0.774 * 100` is 77.4, and an honest
 * tie must not be decided by the dust in the seventeenth digit. A part that is
 * exactly zero (voice for a setting that reads nothing aloud) is never handed a
 * unit.
 */
export function roundPartsToTotal(parts: readonly number[], decimals: number): number[] {
  const scale = 10 ** decimals;
  const target = Math.round(parts.reduce((sum, part) => sum + part, 0) * scale);
  const units = parts.map((part, index) => {
    const exact = part * scale;
    const floor = Math.floor(exact);
    return { index, exact, floor, remainder: Number((exact - floor).toFixed(9)), eligible: part !== 0 };
  });

  let leftover = target - units.reduce((sum, u) => sum + u.floor, 0);
  const extra = new Set<number>();
  for (const u of units
    .filter((unit) => unit.eligible)
    .sort((a, b) => b.remainder - a.remainder || b.exact - a.exact || a.index - b.index)) {
    if (leftover <= 0) break;
    extra.add(u.index);
    leftover -= 1;
  }

  return units.map((u) => (u.floor + (extra.has(u.index) ? 1 : 0)) / scale);
}
